import { motion } from 'framer-motion'

const HabitsScorePhoneMockup = () => {
  return (
    <div className="relative">
      <div className="w-80 h-[640px] bg-gray-900 rounded-[3rem] p-3 shadow-2xl border-2 border-gray-700">
        <div className="w-full h-full bg-black rounded-[2.5rem] overflow-hidden border border-gray-800">
          <motion.div 
            className="p-5"
            animate={{ y: [0, -250, 0] }}
            transition={{ duration: 8, repeat: Infinity, ease: "easeInOut", repeatDelay: 2 }}
          >
            {/* Status Bar */}
            <div className="flex justify-between items-center mb-6 text-sm font-semibold text-white">
              <span>9:41</span>
              <div className="flex gap-1">
                <div className="w-4 h-3 bg-gray-600 rounded-sm"></div>
                <div className="w-4 h-3 bg-gray-600 rounded-sm"></div>
                <div className="w-4 h-3 bg-gray-600 rounded-sm"></div>
              </div>
            </div>

            {/* Header */}
            <motion.div 
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              className="mb-6"
            >
              <div className="text-2xl font-bold text-white mb-1">Money Habits Score</div>
              <div className="text-sm text-gray-400">Your financial health overview</div>
            </motion.div>

            {/* Circular Score */}
            <motion.div
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 0.6 }}
              className="flex justify-center mb-8"
            >
              <div className="relative w-48 h-48">
                {/* Background Circle */}
                <svg className="w-full h-full transform -rotate-90">
                  <circle
                    cx="96"
                    cy="96"
                    r="80"
                    fill="none"
                    stroke="rgba(255,255,255,0.1)"
                    strokeWidth="12"
                  />
                  {/* Progress Circle */}
                  <motion.circle
                    cx="96"
                    cy="96"
                    r="80"
                    fill="none"
                    stroke="url(#score-gradient)"
                    strokeWidth="12"
                    strokeLinecap="round"
                    initial={{ strokeDasharray: "0 502" }}
                    animate={{ strokeDasharray: "427 502" }}
                    transition={{ delay: 0.8, duration: 1.5, ease: "easeOut" }}
                  />
                  <defs>
                    <linearGradient id="score-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" stopColor="#3B82F6" />
                      <stop offset="100%" stopColor="#14B8A6" />
                    </linearGradient>
                  </defs>
                </svg>
                
                {/* Score Text */}
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    transition={{ delay: 1.2, duration: 0.5 }}
                    className="text-5xl font-bold bg-gradient-to-r from-blue-400 to-teal-400 bg-clip-text text-transparent"
                  >
                    85
                  </motion.div>
                  <div className="text-sm text-gray-400 font-medium">out of 100</div>
                  <motion.div
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 1.5, duration: 0.5 }}
                    className="mt-2 px-3 py-1 bg-gradient-to-r from-green-500/20 to-teal-500/20 border border-green-500/30 rounded-full"
                  >
                    <span className="text-xs font-bold text-green-400">Great! 🎯</span>
                  </motion.div>
                </div>
              </div>
            </motion.div>

            {/* Category Breakdown */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.8, duration: 0.5 }}
              className="mb-6"
            >
              <div className="text-sm font-bold text-white mb-4">Score Breakdown</div>
              <div className="space-y-4">
                {/* Savings Habit */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">💰</span>
                      <span className="text-sm font-semibold text-gray-300">Savings Habit</span>
                    </div>
                    <span className="text-sm font-bold text-blue-400">92/100</span>
                  </div>
                  <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: "92%" }}
                      transition={{ delay: 2.0, duration: 1, ease: "easeOut" }}
                      className="h-full bg-gradient-to-r from-blue-400 to-blue-500"
                    />
                  </div>
                </div>

                {/* Spending Control */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">🎯</span>
                      <span className="text-sm font-semibold text-gray-300">Spending Control</span>
                    </div>
                    <span className="text-sm font-bold text-teal-400">88/100</span>
                  </div>
                  <div className="w-full h-bg-gray-800 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: "88%" }}
                      transition={{ delay: 2.2, duration: 1, ease: "easeOut" }}
                      className="h-full bg-gradient-to-r from-teal-400 to-teal-500"
                    />
                  </div>
                </div>

                {/* Budget Adherence */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">📊</span>
                      <span className="text-sm font-semibold text-gray-300">Budget Adherence</span>
                    </div>
                    <span className="text-sm font-bold text-cyan-400">80/100</span>
                  </div>
                  <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: "80%" }}
                      transition={{ delay: 2.4, duration: 1, ease: "easeOut" }}
                      className="h-full bg-gradient-to-r from-cyan-400 to-cyan-500"
                    />
                  </div>
                </div>

                {/* Financial Planning */}
                <div>
                  <div className="flex justify-between items-center mb-2">
                    <div className="flex items-center gap-2">
                      <span className="text-lg">🗓️</span>
                      <span className="text-sm font-semibold text-gray-300">Financial Planning</span>
                    </div>
                    <span className="text-sm font-bold text-sky-400">78/100</span>
                  </div>
                  <div className="w-full h-2 bg-gray-800 rounded-full overflow-hidden">
                    <motion.div
                      initial={{ width: 0 }}
                      animate={{ width: "78%" }}
                      transition={{ delay: 2.6, duration: 1, ease: "easeOut" }}
                      className="h-full bg-gradient-to-r from-sky-400 to-sky-500"
                    />
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Achievement Badges */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 3.0, duration: 0.5 }}
              className="mb-6"
            >
              <div className="text-sm font-bold text-white mb-3">Recent Achievements</div>
              <div className="grid grid-cols-3 gap-3">
                <motion.div
                  whileHover={{ scale: 1.1 }}
                  animate={{ 
                    rotate: [0, -5, 5, -5, 0],
                  }}
                  transition={{ 
                    rotate: { delay: 3.5, duration: 0.5 }
                  }}
                  className="bg-gradient-to-br from-yellow-400/20 to-orange-400/20 border border-yellow-400/30 rounded-xl p-3 text-center"
                >
                  <div className="text-3xl mb-1">🏆</div>
                  <div className="text-xs font-bold text-yellow-400">Saver</div>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.1 }}
                  animate={{ 
                    rotate: [0, 5, -5, 5, 0],
                  }}
                  transition={{ 
                    rotate: { delay: 3.7, duration: 0.5 }
                  }}
                  className="bg-gradient-to-br from-green-400/20 to-emerald-400/20 border border-green-400/30 rounded-xl p-3 text-center"
                >
                  <div className="text-3xl mb-1">✨</div>
                  <div className="text-xs font-bold text-green-400">Consistent</div>
                </motion.div>

                <motion.div
                  whileHover={{ scale: 1.1 }}
                  animate={{ 
                    rotate: [0, -5, 5, -5, 0],
                  }}
                  transition={{ 
                    rotate: { delay: 3.9, duration: 0.5 }
                  }}
                  className="bg-gradient-to-br from-blue-400/20 to-cyan-400/20 border border-blue-400/30 rounded-xl p-3 text-center"
                >
                  <div className="text-3xl mb-1">🎯</div>
                  <div className="text-xs font-bold text-blue-400">On Track</div>
                </motion.div>
              </div>
            </motion.div>

            {/* Improvement Tip */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 3.4, duration: 0.5 }}
              className="bg-gradient-to-r from-blue-500/10 to-teal-500/10 border border-blue-500/20 rounded-2xl p-4"
            >
              <div className="flex gap-3">
                <div className="text-2xl">💡</div>
                <div>
                  <div className="text-sm font-bold text-blue-400 mb-1">Keep it up!</div>
                  <div className="text-xs text-gray-300 leading-relaxed">
                    You're doing great! To reach 90+, try setting aside 5% more each month.
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Extra spacing for scroll animation */}
            <div className="h-32"></div>
          </motion.div>
        </div>
      </div>
    </div>
  )
}

export default HabitsScorePhoneMockup
